/**
 * Montessori language curriculum codes → full form + parent-friendly explanation.
 * Keys are normalized: lowercase, letters/digits only (HG-CONST, hgConst → hgconst).
 */
const SOUND_GLOSSARY = {
  hgconst: {
    fullForm: "Homograms – Consonants",
    explanation:
      "Same consonant letter/symbol representing different sounds. Example: g in goat and gem.",
  },
  hgvowel: {
    fullForm: "Homograms – Vowels",
    explanation: "Same vowel letter/symbol representing different sounds.",
  },
  hpconst: {
    fullForm: "Homophones – Consonants",
    explanation:
      "Different consonant letters/letter combinations representing the same sound. Example: k, c, ck, ch, qu can represent the /k/ sound in different words.",
  },
  hpvowel: {
    fullForm: "Homophones – Vowels",
    explanation:
      "Different vowel letters/letter combinations representing the same sound. Example: ee, ea, ie, ei can represent the long /ee/ sound.",
  },
  vowel1: {
    fullForm: "Vowel – Group 1",
    explanation: "First group of vowel sound/spelling patterns introduced to the child.",
  },
  vowel2: {
    fullForm: "Vowel – Group 2",
    explanation: "Second group of vowel sound/spelling patterns.",
  },
  vowel3: {
    fullForm: "Vowel – Group 3",
    explanation: "Third group of vowel sound/spelling patterns.",
  },
  ae: {
    fullForm: "ae vowel pattern / phonogram",
    explanation: "The ae letter combination used in word building and phonics.",
  },
  ch: {
    fullForm: "ch phonogram",
    explanation: "The ch sound, as in chair and chick.",
  },
  e: {
    fullForm: "e – vowel",
    explanation: "The vowel e and its associated sound/spelling patterns.",
  },
  ee: {
    fullForm: "ee phonogram",
    explanation: "The ee vowel pattern, as in see and tree.",
  },
  ie: {
    fullForm: "ie vowel pattern / phonogram",
    explanation: "The ie spelling pattern, whose sound depends on the word.",
  },
  j: {
    fullForm: "j consonant/phonogram",
    explanation: "The j sound, as in jam and jump.",
  },
  le: {
    fullForm: "le ending pattern",
    explanation: "The le spelling pattern found in words such as table and little.",
  },
  ng: {
    fullForm: "ng phonogram",
    explanation: "The ng sound, as in sing and ring.",
  },
  oe: {
    fullForm: "oe vowel pattern / phonogram",
    explanation: "The oe spelling pattern used in word building.",
  },
  oo: {
    fullForm: "oo phonogram",
    explanation: "The oo vowel pattern, as in moon and book.",
  },
  qu: {
    fullForm: "qu consonant combination",
    explanation: "The qu combination, commonly found in queen and quick.",
  },
  sh: {
    fullForm: "sh phonogram",
    explanation: "The sh sound, as in ship and shop.",
  },
  ssggll: {
    fullForm: "Double consonants – ss, gg, ll",
    explanation:
      "Practice with doubled consonants, particularly ss, gg and ll, in word spelling.",
  },
  th: {
    fullForm: "th phonogram",
    explanation: "The th sound, as in thin and this.",
  },
  ue1: {
    fullForm: "ue – Group 1",
    explanation: "First ue vowel/spelling pattern group.",
  },
  ue2: {
    fullForm: "ue – Group 2",
    explanation: "Second ue vowel/spelling pattern group.",
  },
};

const ALIASES = {
  // movable / master naming variants
  aae: "ae",
  ae1: "ae",
  hpconstonant: "hpconst",
  hgvowels: "hgvowel",
  hgconsonants: "hgconst",
  hpconsonants: "hpconst",
  hpvowels: "hpvowel",
  uegroup1: "ue1",
  uegroup2: "ue2",
  ue11: "ue1",
  ue21: "ue2",
  doubleconsonants: "ssggll",
  doubleconsonantsssggll: "ssggll",
};

export function normalizeLanguageSoundCode(code) {
  return String(code || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

export function getLanguageSoundInfo(code) {
  const key = normalizeLanguageSoundCode(code);
  if (!key) return null;
  const mapped = SOUND_GLOSSARY[key] || SOUND_GLOSSARY[ALIASES[key]];
  return mapped || null;
}

export function getLanguageSoundTooltipTitle(code) {
  const info = getLanguageSoundInfo(code);
  if (!info) return "";
  return `${info.fullForm}\n${info.explanation}`;
}
